import { apiClient, newIdempotencyKey } from './client'
import type { CursorPage } from './accounts'
import type { BranchView, CreateBranchRequest, PatchBranchRequest } from '../types/branch'

export interface ListBranchesParams {
  /** RSQL, already combined by the caller (see lib/rsql.ts). */
  filter?: string | null
  /** `field,-field2` — backend allow-lists name/country/region/createdAt/id (design §2.3 RSQL). */
  sort?: string | null
  cursor?: string | null
  /** Clamped to 200 server-side (Pagination.normalizeLimit); default page size here is 50. */
  limit?: number
}

function toQuery(params: ListBranchesParams): string {
  const q = new URLSearchParams()
  if (params.filter) q.set('filter', params.filter)
  if (params.sort) q.set('sort', params.sort)
  if (params.cursor) q.set('cursor', params.cursor)
  q.set('limit', String(params.limit ?? 50))
  return q.toString()
}

export async function listBranches(
  params: ListBranchesParams,
  signal?: AbortSignal,
): Promise<CursorPage<BranchView>> {
  const { data } = await apiClient.get<CursorPage<BranchView>>(`/branches?${toQuery(params)}`, { signal })
  return data
}

/** Backs `screens/branches/BranchDetailScreen.tsx` — `GET /api/v1/branches/{id}` (already published by `BranchController`). */
export async function getBranch(
  id: string,
  signal?: AbortSignal,
): Promise<{ branch: BranchView; version: number }> {
  const { data, version } = await apiClient.get<BranchView>(`/branches/${id}`, { signal })
  return { branch: data, version: version ?? data.version }
}

export async function createBranch(
  request: CreateBranchRequest,
): Promise<{ branch: BranchView; version: number }> {
  const { data, version } = await apiClient.post<BranchView>('/branches', request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return { branch: data, version: version ?? data.version }
}

export async function patchBranch(
  id: string,
  request: PatchBranchRequest,
  ifMatchVersion: number,
): Promise<{ branch: BranchView; version: number }> {
  const { data, version } = await apiClient.patch<BranchView>(`/branches/${id}`, request, {
    ifMatchVersion,
  })
  return { branch: data, version: version ?? data.version }
}
